import Article from '@/components/article';
import CategoriesList from '@/components/categories-list';
import CategoriesSearch from '@/components/categories-search';
import Footer from '@/components/footer';
import PagesMenu from '@/components/pages-menu';
import Top from '@/components/top';
import TplExamplesValueProposalFooter from '@/components/tpl-examples-value-proposal-footer';
import TplSearchValueProposalFooter from '@/components/tpl-search-value-proposal-footer';
import { useUrl } from '@/hooks/use-url';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect } from 'react';
import Cookies from 'universal-cookie';

export default function Page({ site, page, pricing }) {
  if (!site || !page) return <></>;
  const { getAppUrl } = useUrl();

  // Active le fitrage des modèles
  if (page.path === '/cv-simple')
    site.products_resumes = site.products_resumes.filter((product) => product.category === 'Simple');
  if (page.path === '/cv-original')
    site.products_resumes = site.products_resumes.filter((product) => product.category === 'Original');

  const router = useRouter();
  const cookies = new Cookies();

  useEffect(() => {
    if (router.query.utm_medium) {
      cookies.set('tracking', JSON.stringify(router.query), { path: '/', domain: `.${window.location.hostname}` });
    }
  }, [router.query.utm_medium]);

  return (
    <>
      <Head>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="format-detection" content="telephone=no" />
        {page.meta_robots && page.meta_robots.trim().length > 0 && <meta name="robots" content={page.meta_robots} />}
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />

        {/* SEO */}
        <title>{page.meta_title && page.meta_title}</title>
        <meta name="description" content={page.meta_description} />
        <link rel="canonical" href={page.url.includes('makemycv.com/en') ? page.url.replace('/en', '') : page.url} />
        {site.meta_google_site_verification && site.meta_google_site_verification.length > 0 && (
          <meta name="google-site-verification" content={site.meta_google_site_verification} />
        )}

        {/* Open graph */}
        {page.image_serp && page.image_serp.url && <meta property="og:image" content={`${page.image_serp.url}`} />}
        {page.image_serp && page.image_serp.width && <meta property="og:image:width" content={page.image_serp.width} />}
        {page.image_serp && page.image_serp.height && (
          <meta property="og:image:height" content={page.image_serp.height} />
        )}
        {page.meta_title && <meta property="og:title" content={page.meta_title} />}
        {page.url && (
          <meta
            property="og:url"
            content={page.url.includes('makemycv.com/en') ? page.url.replace('/en', '') : page.url}
          />
        )}
        {page.meta_description && <meta property="og:description" content={page.meta_description} />}
        <meta property="og:site_name" content={site.title} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={process.env.NEXT_PUBLIC_LOCALE} />
        {page.date && <meta property="article:published_time" content={page.get_iso_date} />}
        {page.date_last_update && <meta property="article:modified_time" content={page.get_iso_date_last_update} />}
        {page.author && <meta property="article:author" content={page.author.fullname} />}
        <meta name="p:domain_verify" content="c309a8221aea76f6c5ae1ef4239116fa" />

        {/* Structured data */}
        {page.structuredDataJSON && (
          <Script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${JSON.stringify(page.structuredDataJSON)}`,
            }}
          />
        )}

        {/* Breadcrumb */}
        {page.BreadcrumbListJSON && (
          <Script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${JSON.stringify(page.BreadcrumbListJSON)}`,
            }}
          />
        )}

        {/* FAQ */}
        {page.FAQPageJSON && (
          <Script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `${JSON.stringify(page.FAQPageJSON)}`,
            }}
          />
        )}
      </Head>

      <header>
        <Top site={site} page={page} show_login_only={false} />
      </header>

      {page.template === 'Article' && (
        <main>
          <Article
            site={site}
            page={page}
            showBreadcrumb={true}
            showHeader={true}
            showAuthor={true}
            showSummary={true}
            showFooter={true}
            showHeadersNav={false}
            showTitleInContent={false}
            showDateUpdate={false}
          />
        </main>
      )}

      {page.template === 'Presse' && page.content_press && site && (
        <section className="full ux-spec-section">
          <div className="section-inner">
            <div className="ux-spec-header">
              <h1 className="ux-spec-title">{page.title}</h1>
              <Link href="/contact">
                <button className="cta">
                  <span className="cta-text">{page.content_press.contact_btn}</span>
                  <img
                    src="/assets/icons/arrow-right-white.svg"
                    alt="arrow right"
                    width="22"
                    height="22"
                    loading="lazy"
                  />
                </button>
              </Link>
            </div>

            <div className="header-bg header-bg-left">
              <img
                src="/assets/icons/ux-spec/header-bg-left.svg"
                alt="background left"
                width="178"
                height="224"
                loading="lazy"
              />
            </div>
            <div className="header-bg header-bg-right">
              <img
                src="/assets/icons/ux-spec/header-bg-right.svg"
                alt="background right"
                width="184"
                height="234"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {page.template === 'Presse' && page.content_press && site && (
        <div id="articles-presse">
          {page.content_press.list &&
            page.content_press.list.map((article, i) => {
              return (
                <div className="press-article" key={i}>
                  <div className="image">
                    <img
                      src={article.image.url}
                      width={article.image.width / 2}
                      height={article.image.height / 2}
                      alt={article.image.alt}
                    />
                  </div>
                  <div className="content">
                    <span className="media-name">{article.media_name}</span>
                    <div className="title">{article.title}</div>
                    <div className="button">
                      <span className="text">
                        <Link href={article.url} rel="nofollow">
                          {site.tpl_presse_read}
                        </Link>
                      </span>
                      <img src="/assets/icons/arrow-right-purple.svg" width="16" height="16" />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {page.template === 'Recherche' && site && page && page.menu_categories && (
        <section className="full ux-spec-section">
          <div className="section-inner">
            <div className="ux-spec-header">
              <h1 className="ux-spec-title">{page.title}</h1>
              {page.content_intro && (
                <p className="ux-spec-intro" dangerouslySetInnerHTML={{ __html: page.content_intro }}></p>
              )}
              <div className="ux-spec-search">
                <CategoriesSearch site={site} categories={page.menu_categories} />
              </div>
            </div>

            <div className="header-bg header-bg-left">
              <img
                alt="background left"
                src="/assets/icons/ux-spec/header-bg-left.svg"
                width="178"
                height="224"
                loading="lazy"
              />
            </div>
            <div className="header-bg header-bg-right">
              <img
                alt="background right"
                src="/assets/icons/ux-spec/header-bg-right.svg"
                width="184"
                height="234"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {page.template === 'Recherche' && site && page && page.menu_categories && (
        <CategoriesList site={site} categories={page.menu_categories} />
      )}

      {page.template === 'Exemples' && (
        <section className="full ux-spec-section">
          <div className="section-inner">
            <div className="ux-spec-header">
              <h1 className="ux-spec-title">{page.title}</h1>
              {page.content_intro && (
                <p className="ux-spec-intro" dangerouslySetInnerHTML={{ __html: page.content_intro }}></p>
              )}
              <div className="ux-spec-cta">
                <Link href={getAppUrl(`/create/resume?ref=${page.path}`)}>
                  <button className="cta">
                    <span className="cta-text">{site.tpl_examples_header_cta}</span>
                    <img
                      alt="arrow right"
                      src="/assets/icons/arrow-right-white.svg"
                      width="22"
                      height="22"
                      loading="lazy"
                    />
                  </button>
                </Link>
              </div>
            </div>

            <div className="ux-spec-products-nav">
              <ul>
                <li>
                  <Link href="/cv-simple" className={page.path === '/cv-simple' ? 'active' : undefined}>
                    <img alt="simple" src="/assets/icons/ux-spec/simple.svg" width="70" height="70" loading="lazy" />
                    <span className="text">Simple</span>
                  </Link>
                </li>
                <li>
                  <Link href="/cv-original" className={page.path === '/cv-original' ? 'active' : undefined}>
                    <img
                      alt="original"
                      src="/assets/icons/ux-spec/original.svg"
                      width="70"
                      height="70"
                      loading="lazy"
                    />
                    <span className="text">Original</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div className="header-bg header-bg-left">
              <img
                alt="background left"
                src="/assets/icons/ux-spec/header-bg-left.svg"
                width="178"
                height="224"
                loading="lazy"
              />
            </div>
            <div className="header-bg header-bg-right">
              <img
                alt="background right"
                src="/assets/icons/ux-spec/header-bg-right.svg"
                width="184"
                height="234"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      )}

      {['Recherche'].includes(page.template) && (
        <main>
          <Article
            site={site}
            page={page}
            showHeadersNav={false}
            showHeader={false}
            showBreadcrumb={false}
            showSummary={false}
            showFooter={false}
            showAuthor={false}
            showDateUpdate={false}
            showTitleInContent={false}
          />
        </main>
      )}

      {page.template === 'Recherche' && site.tpl_search_value_proposal_footer && (
        <TplSearchValueProposalFooter site={site} />
      )}

      {page.template === 'Exemples' && (
        <section className="centered resumes-products-list">
          <div className="section-inner">
            <div className="products-list">
              {site.products_resumes &&
                site.products_resumes.map((product) => {
                  const s3ImageUrl = `https://mmc-preview-s3-storage.s3.eu-central-1.amazonaws.com/templates/resumes/${site?.html_lang}/${product.title.toLowerCase()}.jpeg`;
                  return (
                    <div className="product" key={product.title}>
                      <div className="image">
                        <img
                          src={s3ImageUrl}
                          alt={product.image.alt || product.title}
                          width={product.image.width || 325}
                          height={product.image.height || 466}
                          loading="lazy"
                        />
                      </div>
                      <p className="product-title">{product.title}</p>
                      <p className="product-description">{product.description}</p>
                      <div className="product-cta">
                        <Link href={getAppUrl(`/create/resume?ref=${page.path}&template=${product.title}`)}>
                          <button className="cta">
                            <span className="cta-text">{site.tpl_examples_products_list_cta}</span>
                            <img
                              alt="arrow right"
                              src="/assets/icons/arrow-right-white.svg"
                              width="18"
                              height="18"
                              loading="lazy"
                            />
                          </button>
                        </Link>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {page.template === 'Presse' && site.tpl_examples_value_proposal_footer && (
        <TplExamplesValueProposalFooter site={site} />
      )}

      {['Exemples'].includes(page.template) && (
        <main>
          <Article
            site={site}
            page={page}
            showHeadersNav={false}
            showHeader={false}
            showBreadcrumb={false}
            showSummary={false}
            showFooter={false}
            showAuthor={false}
            showDateUpdate={false}
            showTitleInContent={false}
          />
        </main>
      )}

      {['Page'].includes(page.template) && site?.menu_pages?.length > 0 && <PagesMenu />}

      {['Page'].includes(page.template) && (
        <main>
          <Article
            site={site}
            page={page}
            showHeader={true}
            showHeadersNav={true}
            showDateUpdate={true}
            showAuthor={false}
            showBreadcrumb={false}
            showSummary={false}
            showTitleInContent={false}
            showFooter={false}
          />
        </main>
      )}

      {['Auteur'].includes(page.template) && (
        <main data-template={page.template}>
          <Article
            site={site}
            page={page}
            showHeader={true}
            showHeadersNav={false}
            showDateUpdate={false}
            showBreadcrumb={false}
            showSummary={false}
            showAuthor={false}
            showTitleInContent={false}
            showFooter={false}
          />
        </main>
      )}

      {['Tarifs'].includes(page.template) && pricing && (
        <main className="py-5 lg:py-0">
          <div className="relative bg-[#F6F7FA] py-10 lg:py-20">
            <svg
              className="absolute right-0 top-0 hidden lg:block"
              xmlns="http://www.w3.org/2000/svg"
              width="200"
              height="234"
              fill="none"
              viewBox="0 0 200 234"
            >
              <path
                fill="#4B4FF2"
                fillRule="evenodd"
                d="M180.295-16H19.613L.58 131.663c-4.789 37.162 20.596 71.645 56.415 76.617l181.365 25.171c2.564.356 5.112.505 7.64.549V52.18C246 14.682 216.431-16 180.295-16z"
                clipRule="evenodd"
              ></path>
            </svg>

            <svg
              className="absolute bottom-0 left-0 hidden lg:block"
              xmlns="http://www.w3.org/2000/svg"
              width="312"
              height="55"
              fill="none"
              viewBox="0 0 312 55"
            >
              <path
                stroke="#4B4FF2"
                strokeLinecap="round"
                strokeWidth="6.171"
                d="M308.213 3.166C265.626 11.996 22.074 71.232 4.717 84.487-16.979 101.056 250.225 50.11 265.33 53.805c12.083 2.958-200.754 74.012-213.004 79.015-12.25 5.004 157.948-13.326 170.796 42.428"
              ></path>
            </svg>

            <div className="m-auto flex max-w-[920px] flex-col gap-5 px-5">
              <div className="flex flex-col justify-center gap-4 md:text-center">
                <div className="font-[Spoof] text-3xl font-bold lg:text-[42px]">{pricing.page_title}</div>
                <div className="text-base text-[#797F8F] lg:text-lg">{pricing.page_description}</div>
              </div>

              <div className="flex flex-col gap-0 md:flex-row md:gap-6">
                {pricing.plans.map((plan) => {
                  return (
                    <div
                      style={{
                        animation: plan.featured ? 'lightpulse 3s infinite' : 'none',
                      }}
                      key={plan.id}
                      data-featured={plan.featured ? 'yes' : 'no'}
                      className="relative mt-5 flex flex-col justify-between gap-5 rounded-2xl bg-white p-5 pt-8 data-[featured=yes]:bg-[#0E2053] md:w-1/2 md:p-8"
                    >
                      {plan.badge && (
                        <div className="absolute -top-[19px] left-0 flex w-full items-center justify-center">
                          <div className="flex h-[38px] items-center rounded-full border border-[#4B4FF2] bg-[#ECF1FD] px-3 py-2 text-[#4B4FF2]">
                            🔥 {plan.badge}
                          </div>
                        </div>
                      )}
                      <div className="flex w-full flex-col gap-1 text-center">
                        <div
                          className="w-full text-[34px] font-semibold data-[featured=yes]:text-white"
                          data-featured={plan.featured ? 'yes' : 'no'}
                        >
                          {plan.price}
                          {plan.price_subtext ? <span className="ml-1.5 text-sm">{plan.price_subtext}</span> : null}
                        </div>
                        <div className="w-full text-sm font-medium text-[#7684AC]">
                          {typeof plan.price_description === 'string'
                            ? plan.price_description
                            : plan.price_description.message}
                        </div>
                      </div>

                      <div
                        className="flex flex-col gap-5 text-sm data-[featured=yes]:text-white"
                        data-featured={plan.featured ? 'yes' : 'no'}
                      >
                        {plan.description &&
                          plan.description.map((item, index) => {
                            return (
                              <div key={index} className="flex flex-row gap-4 text-left">
                                <svg
                                  className="shrink-0"
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="20"
                                  fill="none"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fill="#4B4FF2"
                                    fillRule="evenodd"
                                    d="M7.32 20L10 18.78 12.68 20l1.71-2.396 2.93-.284.284-2.93L20 12.68 18.78 10 20 7.32l-2.396-1.71-.284-2.93-2.93-.285L12.68 0 10 1.22 7.32 0 5.61 2.395l-2.93.284-.284 2.93L0 7.32 1.22 10 0 12.68l2.396 1.71.283 2.93 2.93.284L7.32 20zm1.459-8.423l-2.44-2.44-1.22 1.22 3.66 3.66 6.101-6.1-1.22-1.22-4.881 4.88z"
                                    clipRule="evenodd"
                                  ></path>
                                </svg>
                                <div className="flex flex-col gap-1">
                                  {item.title && <div className="font-bold">{item.title}</div>}
                                  {item.description && (
                                    <div className={index === plan.description.length - 1 ? 'font-bold' : ''}>
                                      {item.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                      <Link
                        href={getAppUrl(`/create/resume?ref=${page.path}`)}
                        className="rounded-lg bg-[#F1F1FE] py-3 text-center font-medium text-[#4B4FF2] hover:cursor-pointer data-[featured=yes]:bg-[#4B4FF2] data-[featured=yes]:text-white"
                        data-featured={plan.featured ? 'yes' : 'no'}
                      >
                        {plan.cta_button}
                      </Link>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-row flex-wrap justify-between gap-2 pt-5 md:justify-center">
                {['visa', 'mastercard', 'amex', 'paypal', 'google-pay'].map((item) => {
                  return (
                    <img
                      key={item}
                      className="shrink-0 grow-0"
                      src={`/assets/images/payments/${item}.png`}
                      width="50"
                      height="32"
                      alt={item}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </main>
      )}

      <Footer site={site} page={page} />
    </>
  );
}

export async function getStaticPaths({ locales }: { locales: string[] }) {
  const allPaths: { params: { path: string }; locale: string }[] = [];

  for (const locale of locales) {
    try {
      const response = await fetch(`${process.env.BACK_HOST}/api/fetch/pages/paths?locale=${locale}`, {
        headers: { authorization: `Bearer ${process.env.BACK_API_KEY}` },
      });
      const data = await response.json();

      if (data?.paths && Array.isArray(data.paths)) {
        const localePaths = data.paths.map((pathData: any) => ({
          params: { path: pathData.params?.path || '' },
          locale,
        }));

        allPaths.push(...localePaths);
      }
    } catch (error) {
      console.error(`Error fetching paths for locale ${locale}:`, error);
    }
  }

  return { paths: allPaths, fallback: false };
}

export async function getStaticProps({ params, locale }) {
  try {
    const initialResponse = await fetch(
      `${process.env.BACK_HOST}/api/fetch/pages/findOneByPath?path=/${params.path}&locale=${locale}`,
      { headers: { authorization: `Bearer ${process.env.BACK_API_KEY}` } }
    );

    const initialData = await initialResponse.json();
    if (!initialResponse.ok) {
      throw new Error(`Failed to fetch page, received status ${initialResponse.status}`);
    }

    if (!initialData.site || !initialData.page) {
      return { notFound: true };
    }

    const messages = (await import(`@/i18n/locales/${locale}/messages.json`)).default;

    // Définir pageData avec un type qui inclut messages
    const pageData = {
      props: {
        site: initialData.site,
        page: initialData.page,
        messages,
      },
    };

    const requirePricingDataFromApp = ['Tarifs'];

    if (requirePricingDataFromApp.includes(initialData.page.template)) {
      const pricingResponse = await fetch(`${process.env.APP_HOST}/api/plans/prices?locale=${locale}`, {
        headers: {
          authorization: `Bearer ${process.env.BACK_API_KEY}`,
        },
      });
      const pricingData = await pricingResponse.json();
      if (!pricingResponse.ok) {
        throw new Error(`Failed to fetch pricing, received status ${pricingResponse.status}`);
      }

      return {
        props: {
          ...pageData.props,
          pricing: pricingData.pricing,
        },
        revalidate: 3600,
      };
    }

    return { props: pageData.props, revalidate: 3600 };
  } catch (error) {
    console.error(error);
    return { notFound: true };
  }
}
